import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { uploadCityBillPdf, getUploadErrorMessage } from './api';
import { MAX_FILE_SIZE_BYTES } from './constants';
import { UploadCityBillErrorIds, UploadItem, UploadItemStatus, UploadParams } from './types';
import useSnackbar from 'hooks/useSnackbar';
import { useDispatch } from 'store';
import { serverApi } from 'store/reducers/api';

interface UploadMeta {
  file: File;
  company_id?: number;
  property_id: number;
  building_ids: number[];
}

interface CityBillsUploadContextValue {
  items: UploadItem[];
  isUploading: boolean;
  maxFiles: number;
  enqueueFiles: (files: File[], params: UploadParams) => string[];
  removeItem: (id: string) => void;
  clearCompleted: () => void;
  clearAll: () => void;
}

interface CityBillsUploadProviderProps {
  maxFiles?: number;
  apiBaseUrl?: string;
  children: React.ReactNode;
}

const CityBillsUploadContext = createContext<CityBillsUploadContextValue | null>(null);

function validatePdfFile(file: File): string | null {
  if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
    return 'Only PDF files are allowed';
  }
  if (file.size > MAX_FILE_SIZE_BYTES) {
    return 'File exceeds maximum size of 3 MB';
  }
  return null;
}

function validateUploadParams(params: UploadParams): string | null {
  if (!Number.isInteger(params.property_id) || params.property_id <= 0) {
    return 'Invalid property_id';
  }
  if (!params.building_ids?.length) {
    return 'At least one building_id is required';
  }
  if (params.building_ids.some((id) => !Number.isInteger(id) || id <= 0)) {
    return 'Invalid building_ids';
  }
  return null;
}

export function CityBillsUploadProvider({ maxFiles = 10, apiBaseUrl, children }: CityBillsUploadProviderProps) {
  const [items, setItems] = useState<UploadItem[]>([]);
  const { showError, showSuccess } = useSnackbar();
  const dispatch = useDispatch();
  const uploadMetaRef = useRef<Map<string, UploadMeta>>(new Map());
  const queueRef = useRef<string[]>([]);
  const processingRef = useRef(false);
  const abortControllersRef = useRef<Map<string, AbortController>>(new Map());
  const apiBaseUrlRef = useRef(apiBaseUrl);
  apiBaseUrlRef.current = apiBaseUrl;

  const updateItem = useCallback((id: string, patch: Partial<UploadItem>) => {
    setItems((prev) => prev.map((item) => (item.id === id ? { ...item, ...patch } : item)));
  }, []);

  const processQueue = useCallback(async () => {
    if (processingRef.current) return;
    processingRef.current = true;

    while (queueRef.current.length > 0) {
      const id = queueRef.current.shift();
      if (!id) continue;

      const meta = uploadMetaRef.current.get(id);
      if (!meta) continue;

      const controller = new AbortController();
      abortControllersRef.current.set(id, controller);

      updateItem(id, { status: 'uploading', progress: 0 });

      try {
        const response = await uploadCityBillPdf(
          meta.file,
          meta.property_id,
          meta.building_ids,
          {
            apiBaseUrl: apiBaseUrlRef.current,
            signal: controller.signal,
            onProgress: (percent) => updateItem(id, { progress: percent })
          },
          meta.company_id
        );

        let message = '';
        let status: UploadItemStatus = 'error';
        switch (response.error) {
          case UploadCityBillErrorIds.SUCCESS:
            status = 'success';
            message = 'City Bill was processed successfully';
            break;
          case UploadCityBillErrorIds.QUEUED:
            status = 'success';
            message = 'City Bill will be processed shortly';
            break;
          case UploadCityBillErrorIds.DUPLICATED:
            message = 'City Bill has been already uploaded';
            break;
          default:
            message = 'PDF file is not a valid City Bill';
            break;
        }

        if (status === 'success') {
          showSuccess(message);
          dispatch(serverApi.util.invalidateTags(['CityBill']));
        } else {
          showError(message);
        }
        updateItem(id, {
          status,
          progress: 100,
          message
        });
      } catch (error) {
        if (controller.signal.aborted) {
          uploadMetaRef.current.delete(id);
          setItems((prev) => prev.filter((i) => i.id !== id));
        } else {
          const errorMessage = getUploadErrorMessage(error);
          showError(errorMessage);
          updateItem(id, {
            status: 'error',
            progress: 100,
            message: errorMessage
          });
        }
      } finally {
        abortControllersRef.current.delete(id);
      }
    }

    processingRef.current = false;
  }, [updateItem, showError, showSuccess, dispatch]);

  const enqueueFiles = useCallback(
    (files: File[], params: UploadParams): string[] => {
      const createdIds: string[] = [];
      const paramsError = validateUploadParams(params);

      setItems((prev) => {
        const slotsLeft = maxFiles - prev.length;
        if (slotsLeft <= 0) return prev;

        const pdfFiles = files.filter((f) => f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf'));

        const newItems: UploadItem[] = [];

        for (const file of pdfFiles.slice(0, slotsLeft)) {
          const id = uuidv4();
          createdIds.push(id);
          const validationError = paramsError ?? validatePdfFile(file);

          if (!validationError) {
            uploadMetaRef.current.set(id, {
              file,
              company_id: params.company_id,
              property_id: params.property_id,
              building_ids: params.building_ids
            });
            queueRef.current.push(id);
          } else {
            showError(validationError);
          }

          newItems.push({
            id,
            file,
            fileName: file.name,
            fileSize: file.size,
            property_id: params.property_id,
            building_ids: params.building_ids,
            progress: 0,
            status: (validationError ? 'error' : 'pending') as UploadItemStatus,
            message: validationError ?? undefined
          });
        }

        return [...prev, ...newItems];
      });

      setTimeout(() => processQueue(), 0);
      return createdIds;
    },
    [maxFiles, processQueue, showError]
  );

  const removeItem = useCallback((id: string) => {
    const controller = abortControllersRef.current.get(id);
    if (controller) {
      controller.abort();
      abortControllersRef.current.delete(id);
    }
    queueRef.current = queueRef.current.filter((qid) => qid !== id);
    uploadMetaRef.current.delete(id);
    setItems((prev) => prev.filter((i) => i.id !== id));
  }, []);

  const clearCompleted = useCallback(() => {
    setItems((prev) => {
      const remaining = prev.filter((i) => i.status !== 'success' && i.status !== 'error');
      const remainingIds = new Set(remaining.map((i) => i.id));
      for (const id of uploadMetaRef.current.keys()) {
        if (!remainingIds.has(id)) uploadMetaRef.current.delete(id);
      }
      return remaining;
    });
  }, []);

  const clearAll = useCallback(() => {
    abortControllersRef.current.forEach((c) => c.abort());
    abortControllersRef.current.clear();
    queueRef.current = [];
    uploadMetaRef.current.clear();
    setItems([]);
  }, []);

  const isUploading = items.some((i) => i.status === 'uploading');

  const value = useMemo(
    () => ({
      items,
      isUploading,
      maxFiles,
      enqueueFiles,
      removeItem,
      clearCompleted,
      clearAll
    }),
    [items, isUploading, maxFiles, enqueueFiles, removeItem, clearCompleted, clearAll]
  );

  return <CityBillsUploadContext.Provider value={value}>{children}</CityBillsUploadContext.Provider>;
}

export function useCityBillsUpload(): CityBillsUploadContextValue {
  const ctx = useContext(CityBillsUploadContext);
  if (!ctx) {
    throw new Error('useCityBillsUpload must be used within CityBillsUploadProvider');
  }
  return ctx;
}

/** Returns null when no app-level provider is mounted. */
export function useCityBillsUploadOptional(): CityBillsUploadContextValue | null {
  return useContext(CityBillsUploadContext);
}
